import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ========== Menu Configuration ==========

export const getAvailableMenus = async () => {
  const response = await api.get('/api/users/menus');
  return response.data;
};

// ========== User Management ==========

export const getTenantUsers = async (tenantId, params = {}) => {
  const response = await api.get(`/api/users/tenant/${tenantId}/members`, { params });
  return response.data;
};

export const updateUserRole = async (tenantId, userId, role) => {
  const response = await api.put(`/api/users/tenant/${tenantId}/members/${userId}/role`, { role });
  return response.data;
};

export const updateUserMenuAccess = async (tenantId, userId, menuAccess) => {
  const response = await api.put(`/api/users/tenant/${tenantId}/members/${userId}/menu-access`, {
    menu_access: menuAccess
  });
  return response.data;
};

export const getUserMenuAccess = async (tenantId, userId) => {
  const response = await api.get(`/api/users/tenant/${tenantId}/members/${userId}/menu-access`);
  return response.data;
};

export const getMyMenuAccess = async (tenantId = null) => {
  const params = tenantId ? { tenant_id: tenantId } : {};
  const response = await api.get('/api/users/me/menu-access', { params });
  return response.data;
};

// ========== Access Level Management ==========

export const getAccessLevels = async () => {
  const response = await api.get('/api/users/access-levels');
  return response.data;
};

export const updateUserAccessLevel = async (tenantId, userId, accessLevel) => {
  const response = await api.put(`/api/users/tenant/${tenantId}/members/${userId}/access-level`, {
    access_level: accessLevel
  });
  return response.data;
};

export const getUserAccessLevel = async (tenantId, userId) => {
  const response = await api.get(`/api/users/tenant/${tenantId}/members/${userId}/access-level`);
  return response.data;
};

// ========== Tenant License/Module Management ==========

export const getTenantLicense = async (tenantId) => {
  const response = await api.get(`/api/users/tenant/${tenantId}/license`);
  return response.data;
};

export const updateTenantModules = async (tenantId, enabledModules) => {
  const response = await api.put(`/api/users/tenant/${tenantId}/modules`, {
    enabled_modules: enabledModules
  });
  return response.data;
};

export const updateTenantLicenseTier = async (tenantId, licenseTier) => {
  const response = await api.put(`/api/users/tenant/${tenantId}/license-tier`, {
    license_tier: licenseTier
  });
  return response.data;
};

// ========== Admin Portal Access ==========

export const canAccessAdminPortal = async () => {
  const response = await api.get('/api/users/can-access-admin-portal');
  return response.data;
};

export const canCreateTenant = async () => {
  const response = await api.get('/api/users/can-create-tenant');
  return response.data;
};

// ========== Directory Import ==========

export const getMockDirectoryUsers = async (tenantId, provider) => {
  const response = await api.get(`/api/users/tenant/${tenantId}/directory/mock/${provider}`);
  return response.data;
};

export const importFromDirectory = async (tenantId, provider, users, defaultRole = 'viewer') => {
  const response = await api.post(`/api/users/tenant/${tenantId}/import/directory`, {
    provider,
    users: users.map(u => ({
      email: u.email,
      full_name: u.full_name,
      department: u.department || null,
      job_title: u.job_title || null,
      source: provider
    })),
    default_role: defaultRole
  });
  return response.data;
};

export const importFromCSV = async (tenantId, file, defaultRole = 'viewer') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('default_role', defaultRole);
  const response = await api.post(`/api/users/tenant/${tenantId}/import/csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const downloadCSVTemplate = () => {
  const csvContent = "email,full_name,department,job_title,role\njohn@example.com,John Smith,Engineering,Software Engineer,team_member\njane@example.com,Jane Doe,Security,Security Analyst,viewer\n";
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'user_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

// ========== SCIM Provisioning ==========

export const getScimConfigs = async (tenantId) => {
  const response = await api.get('/api/scim-admin/config', { params: { tenant_id: tenantId } });
  return response.data;
};

export const createScimConfig = async (tenantId, provider) => {
  const response = await api.post('/api/scim-admin/config', { tenant_id: tenantId, provider });
  return response.data;
};

export const toggleScimConfig = async (configId, enabled) => {
  const response = await api.put(`/api/scim-admin/config/${configId}/toggle`, { enabled });
  return response.data;
};

export const deleteScimConfig = async (configId) => {
  const response = await api.delete(`/api/scim-admin/config/${configId}`);
  return response.data;
};

export const getScimEvents = async (tenantId, limit = 30) => {
  const response = await api.get('/api/scim-admin/events', { params: { tenant_id: tenantId, limit } });
  return response.data;
};

export const getScimGroups = async (tenantId) => {
  const response = await api.get('/api/scim-admin/groups', { params: { tenant_id: tenantId } });
  return response.data;
};

export const getRoleMappings = async (tenantId) => {
  const response = await api.get('/api/scim-admin/role-mappings', { params: { tenant_id: tenantId } });
  return response.data;
};

export const createRoleMapping = async (tenantId, groupName, role) => {
  const response = await api.post('/api/scim-admin/role-mappings', { tenant_id: tenantId, group_name: groupName, role });
  return response.data;
};

export const deleteRoleMapping = async (groupName, tenantId) => {
  const response = await api.delete(`/api/scim-admin/role-mappings/${groupName}`, { params: { tenant_id: tenantId } });
  return response.data;
};

// ========== Role Definitions ==========

export const TENANT_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full resource access, manage team', color: 'bg-purple-100 text-purple-700' },
  { value: 'admin', label: 'Admin', description: 'Full tenant administration, user management', color: 'bg-red-100 text-red-700' },
  { value: 'team_member', label: 'Team Member', description: 'Create and edit resources', color: 'bg-blue-100 text-blue-700' },
  { value: 'auditor', label: 'Auditor', description: 'Read-only audit access', color: 'bg-amber-100 text-amber-700' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access', color: 'bg-gray-100 text-gray-700' }
];

export const ACCESS_LEVELS = [
  { value: 'read_write', label: 'Read/Write', description: 'Full access - can view and modify', color: 'bg-green-100 text-green-700' },
  { value: 'read_only', label: 'Read Only', description: 'Can only view, no modifications', color: 'bg-blue-100 text-blue-700' },
  { value: 'auditor', label: 'Auditor', description: 'Read-only audit access', color: 'bg-amber-100 text-amber-700' }
];

export const DIRECTORY_PROVIDERS = [
  {
    id: 'csv',
    name: 'CSV File',
    icon: '📄',
    description: 'Import users from a CSV file (email, name, department, role)'
  }
];

export const getRoleColor = (role) => {
  const found = TENANT_ROLES.find(r => r.value === role);
  return found?.color || 'bg-gray-100 text-gray-700';
};

export const getRoleLabel = (role) => {
  const found = TENANT_ROLES.find(r => r.value === role);
  return found?.label || role;
};

export const getAccessLevelColor = (accessLevel) => {
  const found = ACCESS_LEVELS.find(l => l.value === accessLevel);
  return found?.color || 'bg-gray-100 text-gray-700';
};

export const getAccessLevelLabel = (accessLevel) => {
  const found = ACCESS_LEVELS.find(l => l.value === accessLevel);
  return found?.label || accessLevel;
};

export default {
  getAvailableMenus,
  getTenantUsers,
  updateUserRole,
  updateUserMenuAccess,
  getUserMenuAccess,
  getMyMenuAccess,
  getAccessLevels,
  updateUserAccessLevel,
  getUserAccessLevel,
  getTenantLicense,
  updateTenantModules,
  updateTenantLicenseTier,
  canAccessAdminPortal,
  canCreateTenant,
  getMockDirectoryUsers,
  importFromDirectory,
  importFromCSV,
  downloadCSVTemplate,
  getScimConfigs,
  createScimConfig,
  toggleScimConfig,
  deleteScimConfig,
  getScimEvents,
  getScimGroups,
  getRoleMappings,
  createRoleMapping,
  deleteRoleMapping,
  TENANT_ROLES,
  ACCESS_LEVELS,
  DIRECTORY_PROVIDERS,
  getRoleColor,
  getRoleLabel,
  getAccessLevelColor,
  getAccessLevelLabel
};
